
/**
 * PortalDropdown - Reusable Portal-based Dropdown Component
 *
 * Renders dropdown content via React Portal to escape overflow:hidden ancestors.
 * Handles positioning, viewport detection, animations, and accessibility.
 *
 * @example
 * ```tsx
 * <PortalDropdown
 *   isOpen={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   triggerRef={buttonRef}
 *   placement="bottom"
 * >
 *   <DropdownContent />
 * </PortalDropdown>
 * ```
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../../lib/utils';

// ============================================================================
// TYPES
// ============================================================================

export type DropdownPlacement = 'bottom' | 'top' | 'bottom-start' | 'bottom-end' | 'top-start' | 'top-end';

export interface DropdownPosition {
  top: number;
  left: number;
  width: number;
  actualPlacement: DropdownPlacement;
}

export interface PortalDropdownProps {
  /** Whether the dropdown is open */
  isOpen: boolean;
  /** Callback when dropdown should close */
  onClose: () => void;
  /** Ref to the trigger element for positioning */
  triggerRef: React.RefObject<HTMLElement | null>;
  /** Dropdown content */
  children: React.ReactNode;
  /** Preferred placement relative to trigger */
  placement?: DropdownPlacement;
  /** Gap between trigger and dropdown (px) */
  offset?: number;
  /** Minimum width of dropdown (px) */
  minWidth?: number;
  /** Maximum width of dropdown (px) */
  maxWidth?: number;
  /** Whether to match trigger width */
  matchTriggerWidth?: boolean;
  /** Additional className for dropdown container */
  className?: string;
  /** Whether to show backdrop overlay */
  showBackdrop?: boolean;
  /** Custom z-index for dropdown */
  zIndex?: number;
  /** Approximate dropdown height for flip calculation */
  estimatedHeight?: number;
  /** Whether to close on ESC key */
  closeOnEsc?: boolean;
  /** Whether to close when clicking outside */
  closeOnClickOutside?: boolean;
}

// ============================================================================
// CONSTANTS
// ============================================================================

const DEFAULT_OFFSET = 8;
const DEFAULT_MIN_WIDTH = 200;
const DEFAULT_Z_INDEX = 99999;
const DEFAULT_ESTIMATED_HEIGHT = 300;
const VIEWPORT_PADDING = 12;

// ============================================================================
// ANIMATION VARIANTS
// ============================================================================

const getAnimationVariants = (placement: DropdownPlacement) => {
  const isTop = placement.startsWith('top');

  return {
    initial: {
      opacity: 0,
      y: isTop ? 10 : -10,
      scale: 0.95
    },
    animate: {
      opacity: 1,
      y: 0,
      scale: 1
    },
    exit: {
      opacity: 0,
      y: isTop ? 10 : -10,
      scale: 0.95
    }
  };
};

// ============================================================================
// POSITION CALCULATOR
// ============================================================================

const calculatePosition = (
  triggerRect: DOMRect,
  preferredPlacement: DropdownPlacement,
  options: {
    offset: number;
    minWidth: number;
    maxWidth?: number;
    matchTriggerWidth: boolean;
    estimatedHeight: number;
  }
): DropdownPosition => {
  const { offset, minWidth, maxWidth, matchTriggerWidth, estimatedHeight } = options;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Calculate width
  let width = matchTriggerWidth
    ? triggerRect.width
    : Math.max(triggerRect.width, minWidth);

  if (maxWidth) {
    width = Math.min(width, maxWidth);
  }

  // Determine vertical placement (flip if needed)
  const spaceBelow = viewportHeight - triggerRect.bottom - offset;
  const spaceAbove = triggerRect.top - offset;

  let actualPlacement = preferredPlacement;

  // Check if we need to flip vertically
  if (preferredPlacement.startsWith('bottom') && spaceBelow < estimatedHeight && spaceAbove > spaceBelow) {
    actualPlacement = preferredPlacement.replace('bottom', 'top') as DropdownPlacement;
  } else if (preferredPlacement.startsWith('top') && spaceAbove < estimatedHeight && spaceBelow > spaceAbove) {
    actualPlacement = preferredPlacement.replace('top', 'bottom') as DropdownPlacement;
  }

  // Calculate top position
  let top: number;
  if (actualPlacement.startsWith('top')) {
    top = triggerRect.top - estimatedHeight - offset;
  } else {
    top = triggerRect.bottom + offset;
  }

  // Calculate left position based on alignment
  let left: number;
  if (actualPlacement.endsWith('-start')) {
    left = triggerRect.left;
  } else if (actualPlacement.endsWith('-end')) {
    left = triggerRect.right - width;
  } else {
    // Center aligned
    left = triggerRect.left + (triggerRect.width - width) / 2;
  }

  // Clamp horizontal position to viewport
  left = Math.max(VIEWPORT_PADDING, Math.min(left, viewportWidth - width - VIEWPORT_PADDING));

  // Clamp vertical position to viewport
  top = Math.max(VIEWPORT_PADDING, Math.min(top, viewportHeight - estimatedHeight - VIEWPORT_PADDING));

  return { top, left, width, actualPlacement };
};

// ============================================================================
// PORTAL DROPDOWN COMPONENT
// ============================================================================

export const PortalDropdown: React.FC<PortalDropdownProps> = ({
  isOpen,
  onClose,
  triggerRef,
  children,
  placement = 'bottom',
  offset = DEFAULT_OFFSET,
  minWidth = DEFAULT_MIN_WIDTH,
  maxWidth,
  matchTriggerWidth = false,
  className,
  showBackdrop = true,
  zIndex = DEFAULT_Z_INDEX,
  estimatedHeight = DEFAULT_ESTIMATED_HEIGHT,
  closeOnEsc = true,
  closeOnClickOutside = true
}) => {
  const [isMounted, setIsMounted] = useState(false);
  const [position, setPosition] = useState<DropdownPosition | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // SSR Safety - Only render Portal after client mount
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Calculate position
  const updatePosition = useCallback(() => {
    if (!triggerRef.current) return;

    const rect = triggerRef.current.getBoundingClientRect();
    const newPosition = calculatePosition(rect, placement, {
      offset,
      minWidth,
      maxWidth,
      matchTriggerWidth,
      estimatedHeight
    });

    setPosition(newPosition);
  }, [triggerRef, placement, offset, minWidth, maxWidth, matchTriggerWidth, estimatedHeight]);

  // Update position when dropdown opens or dependencies change
  useEffect(() => {
    if (isOpen) {
      // Calculate immediately
      updatePosition();
      // Also calculate after a frame to ensure accurate measurements
      requestAnimationFrame(updatePosition);
    }
  }, [isOpen, updatePosition]);

  // Handle scroll and resize
  useEffect(() => {
    if (!isOpen) return;

    const handleScrollOrResize = () => {
      updatePosition();
    };

    window.addEventListener('scroll', handleScrollOrResize, true);
    window.addEventListener('resize', handleScrollOrResize);

    return () => {
      window.removeEventListener('scroll', handleScrollOrResize, true);
      window.removeEventListener('resize', handleScrollOrResize);
    };
  }, [isOpen, updatePosition]);

  // Handle ESC key
  useEffect(() => {
    if (!isOpen || !closeOnEsc) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, closeOnEsc, onClose]);

  // Handle click outside
  const handleBackdropClick = useCallback((e: React.MouseEvent) => {
    if (closeOnClickOutside) {
      e.preventDefault();
      e.stopPropagation();
      onClose();
    }
  }, [closeOnClickOutside, onClose]);

  // Don't render on server
  if (!isMounted) return null;

  const variants = getAnimationVariants(position?.actualPlacement || placement);

  return createPortal(
    <AnimatePresence>
      {isOpen && position && (
        <>
          {/* Backdrop for click-outside detection */}
          {showBackdrop && (
            <div
              className="fixed inset-0"
              style={{ zIndex: zIndex - 1 }}
              onClick={handleBackdropClick}
              aria-hidden="true"
            />
          )}

          {/* Dropdown Content */}
          <motion.div
            ref={dropdownRef}
            role="listbox"
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            className={cn(
              'bg-white dark:bg-gray-800',
              'border border-gray-200 dark:border-gray-600',
              'rounded-2xl shadow-xl',
              'overflow-hidden',
              className
            )}
            style={{
              position: 'fixed',
              top: position.top,
              left: position.left,
              width: position.width,
              zIndex,
              maxHeight: 'calc(100vh - 100px)',
              overflowY: 'auto'
            }}
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

// ============================================================================
// HOOK FOR DROPDOWN STATE MANAGEMENT
// ============================================================================

export interface UsePortalDropdownOptions {
  /** Initial open state */
  defaultOpen?: boolean;
  /** Callback when open state changes */
  onOpenChange?: (isOpen: boolean) => void;
}

export interface UsePortalDropdownReturn {
  /** Whether dropdown is open */
  isOpen: boolean;
  /** Open the dropdown */
  open: () => void;
  /** Close the dropdown */
  close: () => void;
  /** Toggle the dropdown */
  toggle: () => void;
  /** Ref for the trigger element */
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  /** Props to spread on trigger element */
  triggerProps: {
    ref: React.RefObject<HTMLButtonElement | null>;
    onClick: () => void;
    'aria-expanded': boolean;
    'aria-haspopup': 'listbox';
  };
  /** Props to spread on PortalDropdown */
  dropdownProps: {
    isOpen: boolean;
    onClose: () => void;
    triggerRef: React.RefObject<HTMLButtonElement | null>;
  };
}

/**
 * Hook for managing dropdown state with proper accessibility attributes
 *
 * @example
 * ```tsx
 * const { triggerProps, dropdownProps, isOpen } = usePortalDropdown();
 *
 * return (
 *   <>
 *     <button {...triggerProps}>Open Menu</button>
 *     <PortalDropdown {...dropdownProps}>
 *       <MenuContent />
 *     </PortalDropdown>
 *   </>
 * );
 * ```
 */
export const usePortalDropdown = (
  options: UsePortalDropdownOptions = {}
): UsePortalDropdownReturn => {
  const { defaultOpen = false, onOpenChange } = options;
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const open = useCallback(() => {
    setIsOpen(true);
    onOpenChange?.(true);
  }, [onOpenChange]);

  const close = useCallback(() => {
    setIsOpen(false);
    onOpenChange?.(false);
  }, [onOpenChange]);

  const toggle = useCallback(() => {
    const newState = !isOpen;
    setIsOpen(newState);
    onOpenChange?.(newState);
  }, [isOpen, onOpenChange]);

  return {
    isOpen,
    open,
    close,
    toggle,
    triggerRef,
    triggerProps: {
      ref: triggerRef,
      onClick: toggle,
      'aria-expanded': isOpen,
      'aria-haspopup': 'listbox' as const
    },
    dropdownProps: {
      isOpen,
      onClose: close,
      triggerRef
    }
  };
};

// ============================================================================
// EXPORTS
// ============================================================================

export default PortalDropdown;
